import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MOCK_PROJECTS, MOCK_FILMS } from '../mock/data';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [associatedFilm, setAssociatedFilm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Dans un projet réel, vous feriez un appel API ici
    // Pour le moment, on utilise les données mockées
    const projectId = parseInt(id);
    const foundProject = MOCK_PROJECTS.find(project => project.id === projectId);
    
    if (foundProject) {
      setProject(foundProject);
      
      // Trouver le film associé
      if (foundProject.associatedFilm) {
        const foundFilm = MOCK_FILMS.find(
          film => film.id === foundProject.associatedFilm
        );
        setAssociatedFilm(foundFilm);
      }
    }
    
    setLoading(false);
  }, [id]);

  // Fonction pour calculer le pourcentage de financement
  const getFundingPercentage = () => {
    if (!project) return 0;
    return Math.min(Math.round((project.raised / project.goal) * 100), 100);
  };

  // Fonction pour calculer le temps restant
  const getTimeRemaining = () => {
    if (!project) return "";
    
    const endDate = new Date(project.endDate);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return "Campagne terminée";
    if (diffDays === 1) return "Dernier jour";
    return `${diffDays} jours restants`;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-red-700">Projet non trouvé. Ce projet n'existe pas ou a été supprimé.</p>
        </div>
        <div className="mt-6">
          <Link to="/" className="text-amber-600 hover:text-amber-700 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      {/* En-tête du projet */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{project.title}</h1>
        <div className="flex flex-wrap items-center text-gray-600 text-sm mb-4">
          <span className="mr-4">Réalisé par {project.director}</span>
          <span>{project.genre}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Colonne gauche: Image et financement */}
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
            <img 
              src={project.poster || "/images/placeholder-project.jpg"} 
              alt={`Affiche du projet ${project.title}`}
              className="w-full h-auto"
            />
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center mb-3">
              <div className="text-2xl font-bold text-amber-600">
                {getFundingPercentage()}%
              </div>
              <div className="text-sm text-gray-500 font-medium">
                {getTimeRemaining()}
              </div>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div 
                className="bg-amber-500 h-3 rounded-full" 
                style={{ width: `${getFundingPercentage()}%` }}
                role="progressbar"
                aria-valuenow={getFundingPercentage()}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
            
            <div className="flex justify-between text-sm mb-6">
              <span className="font-medium">{Number(project.raised).toLocaleString()} FCFA</span>
              <span className="text-gray-500">sur {Number(project.goal).toLocaleString()} FCFA</span>
            </div>
            
            <button 
              className="w-full bg-amber-500 hover:bg-amber-600 text-white py-3 rounded-lg font-medium transition"
            >
              Contribuer à ce projet
            </button>
          </div>
          
          {associatedFilm && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="font-bold text-lg mb-4">Film associé</h3>
              <div className="flex items-center">
                <img 
                  src={associatedFilm.poster || "/images/placeholder-poster.jpg"} 
                  alt={`Affiche du film ${associatedFilm.title}`}
                  className="w-16 h-24 object-cover rounded mr-4"
                />
                <div>
                  <h4 className="font-medium">{associatedFilm.title}</h4>
                  <p className="text-sm text-gray-600 mb-2">{associatedFilm.director}</p>
                  <Link 
                    to={`/films/${associatedFilm.id}`}
                    className="text-amber-600 hover:text-amber-700 text-sm font-medium"
                  >
                    Voir le film
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Colonne droite: Description */}
        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="font-bold text-lg mb-4">Synopsis</h3>
            <p className="text-gray-700 mb-4">
              {project.synopsis}
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="font-bold text-lg mb-4">À propos du projet</h3>
            
            <div className="mb-6">
              <h4 className="font-medium mb-2">Vision du réalisateur</h4>
              <p className="text-gray-700">
                Ce projet représente une vision unique du cinéma africain, mêlant tradition et modernité. 
                À travers cette œuvre, le réalisateur souhaite mettre en lumière des récits authentiques 
                qui résonnent bien au-delà des frontières du continent.
              </p>
            </div>
            
            <div className="mb-6">
              <h4 className="font-medium mb-2">Équipe du projet</h4>
              <p className="text-gray-700">
                Une équipe talentueuse de professionnels africains travaille sur ce projet, 
                combinant expertise technique et sensibilité artistique pour créer une œuvre 
                cinématographique de qualité internationale.
              </p>
            </div>
            
            <div>
              <h4 className="font-medium mb-2">Impact culturel</h4>
              <p className="text-gray-700">
                En soutenant ce projet, vous contribuez directement à l'essor du cinéma africain 
                et à la préservation de son patrimoine culturel. Chaque contribution permet de faire 
                entendre des voix souvent sous-représentées sur la scène cinématographique mondiale.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bouton retour */}
      <div className="mt-8">
        <Link to="/" className="text-amber-600 hover:text-amber-700 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Retour à l'accueil
        </Link>
      </div>
    </div>
  );
};

export default ProjectDetail;
